import React, { useState } from "react";
import { useGlobal } from "utils/globalState";

import { Link } from "@sainsburys-tech/luna-link";
import {
  Card,
  SubmitButton,
  trackEvent,
  reportError,
  Notification,
  NotificationStatuses,
} from "@jsainsburyplc/id-ui-common";
import { Section } from "@sainsburys-tech/luna-section";
import {
  ButtonGroupPrimary,
  ButtonGroupSecondary,
  ButtonGroupWrapper,
} from "@sainsburys-tech/luna-button";

import { OPT_OUT_LS_KEY } from "constants/webAuthn";
import { webAuthnService } from "services/webAuthn.service";
import { WEBAUTHN_ALREADY_REGISTERED } from "constants/messages";
import { categoryTypes, actionTypes } from "constants/tracking";
import { getKeepYourAccountSafeUrl } from "utils/urls";

import RouterLink from "./RouterLink";

const trackRegistration = (result) => {
  trackEvent({
    category: categoryTypes.WEB_AUTHN,
    action: actionTypes[result],
    label: `Device registration ${result.toLowerCase()}`,
  });
};

const trackButtonClick = (label) => {
  trackEvent({
    category: categoryTypes.WEB_AUTHN,
    action: actionTypes.CLICK,
    label,
  });
};

const WebAuthnRegistration = ({ setIsRegistered, handleLogin }) => {
  const [readable_mission_name] = useGlobal("readable_mission_name");

  const [isRegistering, setIsRegistering] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const handleSetUpNow = async (e) => {
    e && e.preventDefault();
    setIsRegistering(true);
    try {
      const register = await webAuthnService.registerDevice();
      const response = await register.json();
      if (response.result) {
        localStorage.setItem("customerId", response.customerId);
        setIsRegistered(true);
      }
      trackRegistration(actionTypes.SUCCESS);
    } catch (error) {
      trackRegistration(actionTypes.ERROR);
      reportError(`${categoryTypes.WEB_AUTHN}: ${error}`);
      setAlreadyRegistered(true);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleSetUpLaterClick = () => {
    trackButtonClick("Set up later");
    handleLogin();
  };

  const handleDontAskAgainClick = () => {
    trackButtonClick(`Don't ask again`);
    localStorage.setItem(OPT_OUT_LS_KEY, true);
    handleLogin();
  };

  const handleLearnMoreClick = () => trackButtonClick(`Learn more`);

  return (
    <>
      <div className="c-hero">
        <h1 className="ln-u-h2">
          Log in with your fingerprint or facial recognition
        </h1>
      </div>

      {alreadyRegistered && (
        <div className="ln-u-margin-bottom*2">
          <Notification type={NotificationStatuses.ERROR_TYPE}>
            {WEBAUTHN_ALREADY_REGISTERED}
          </Notification>
        </div>
      )}

      <div className="web-authn-registration">
        <Card>
          <Section style={{ paddingBottom: "1rem" }}>
            <p>
              If your phone has a fingerprint sensor or has facial recognition,
              you can now use them to log in to {readable_mission_name}.
            </p>
            <p>
              Using your fingerprint or facial recognition is a secure and
              convenient way to log in to {readable_mission_name}.
            </p>
          </Section>
          <Section
            style={{
              textAlign: "center",
            }}
          >
            <ButtonGroupWrapper className="ln-u-hard-top ln-u-margin-bottom">
              <ButtonGroupSecondary>
                <SubmitButton
                  data-testid="set-up-now"
                  isLoading={isRegistering}
                  onClick={handleSetUpNow}
                  label="Set up now"
                  isLoadingLabel="Registering..."
                  fullWidth={true}
                />
              </ButtonGroupSecondary>
              <ButtonGroupPrimary>
                <Link
                  style={{ maxWidth: "22rem" }}
                  data-testid="set-up-later"
                  onClick={handleSetUpLaterClick}
                  className="ln-c-button ln-c-button--outlined ln-c-button--full ln-c-link ln-c-link--bare ln-c-link--alpha"
                >
                  Set up later
                </Link>
              </ButtonGroupPrimary>
            </ButtonGroupWrapper>

            <Link
              onClick={handleDontAskAgainClick}
              className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha dont-ask-again-link"
              data-testid="dont-ask-again"
            >
              Don't ask me again
            </Link>
          </Section>
          <div>
            <RouterLink
              link={getKeepYourAccountSafeUrl()}
              onClick={handleLearnMoreClick}
              className="ln-c-link"
              data-testid="learn-more-account-safe"
            >
              Learn more about keeping your account safe
            </RouterLink>
          </div>
        </Card>
      </div>
    </>
  );
};

export default WebAuthnRegistration;
